import { HubsApi, Configuration as HubsConfiguration } from '@control-center/control-center-client-public-hubs-ts';
import {
    MissionsApi,
    Configuration as MissionsConfiguration,
} from '@control-center/control-center-client-public-missions-ts';
import { Configuration, VehiclesApi } from '@control-center/control-center-client-public-vehicles-ts';
import { fetchAuthSession } from 'aws-amplify/auth';
import axios, { type RawAxiosRequestConfig } from 'axios';
import { v4 as uuid } from 'uuid';

const REQUEST_TIMEOUT_MS = 30_000;

/** One correlation id per request, for cross-service tracing. Shared by every generated client. */
export const correlationHeaders = (): RawAxiosRequestConfig => ({ headers: { 'x-correlation-id': uuid() } });

export class RestError extends Error {
    readonly status: number;
    readonly body: unknown;

    constructor(message: string, status: number, body?: unknown) {
        super(message);
        this.name = 'RestError';
        this.status = status;
        this.body = body;
    }
}

/** Shared by every generated REST client here: one Cognito access token per request. */
export const getServiceAccessToken = async (): Promise<string> => {
    const token = (await fetchAuthSession()).tokens?.accessToken?.toString();
    if (!token) {
        throw new RestError('No Cognito access token available', 401);
    }
    return token;
};

const configuration = new Configuration({
    basePath: import.meta.env.VITE_VEHICLE_SERVICE_ENDPOINT?.replace(/\/+$/, ''),
    accessToken: getServiceAccessToken,
    baseOptions: {
        timeout: REQUEST_TIMEOUT_MS,
        headers: { accept: 'application/json' },
    },
});

export const vehiclesApi = new VehiclesApi(configuration);

const missionConfiguration = new MissionsConfiguration({
    basePath: import.meta.env.VITE_MISSION_HUB_SERVICE_ENDPOINT?.replace(/\/+$/, ''),
    accessToken: getServiceAccessToken,
    baseOptions: {
        timeout: REQUEST_TIMEOUT_MS,
        headers: { accept: 'application/json' },
    },
});

export const missionsApi = new MissionsApi(missionConfiguration);

const hubConfiguration = new HubsConfiguration({
    basePath: import.meta.env.VITE_MISSION_HUB_SERVICE_ENDPOINT?.replace(/\/+$/, ''),
    accessToken: getServiceAccessToken,
    baseOptions: {
        timeout: REQUEST_TIMEOUT_MS,
        headers: { accept: 'application/json' },
    },
});

export const hubsRestApi = new HubsApi(hubConfiguration);

/**
 * Hand-rolled because no published monitoring client exists; its one operation
 * (`GET /api/v1/health/status`) declares no response body, so only the status code is read.
 * Named `monitoringApi` since `systemApi` is already the RTK Query slice in `services/system.ts`.
 */
export const monitoringApi = axios.create({
    baseURL: import.meta.env.VITE_MONITORING_SERVICE_ENDPOINT?.replace(/\/+$/, ''),
    timeout: REQUEST_TIMEOUT_MS,
});

monitoringApi.interceptors.request.use(async config => {
    config.headers.set('Authorization', `Bearer ${await getServiceAccessToken()}`);
    config.headers.set('Accept', 'application/json');

    // Remove the axios default Content-Type on requests without a body to avoid triggering CORS
    // preflights the backend doesn't handle, as `features/domain-simulation/apiClient.ts` does.
    if (!config.data) {
        config.headers.delete('Content-Type');
    }

    return config;
});

export const managementRestApi = axios.create({
    baseURL: import.meta.env.VITE_MANAGEMENT_SERVICE_ENDPOINT?.replace(/\/+$/, ''),
    timeout: REQUEST_TIMEOUT_MS,
});

managementRestApi.interceptors.request.use(async config => {
    config.headers.set('Authorization', `Bearer ${await getServiceAccessToken()}`);
    config.headers.set('Accept', 'application/json');

    // Remove the axios default Content-Type on requests without a body to avoid triggering CORS
    // preflights the backend doesn't handle, as `monitoringApi` above does.
    if (!config.data) {
        config.headers.delete('Content-Type');
    }

    return config;
});

export const commandsRestApi = axios.create({
    baseURL: import.meta.env.VITE_VEHICLE_SERVICE_ENDPOINT?.replace(/\/+$/, ''),
    timeout: REQUEST_TIMEOUT_MS,
});

commandsRestApi.interceptors.request.use(async config => {
    config.headers.set('Authorization', `Bearer ${await getServiceAccessToken()}`);
    config.headers.set('Accept', 'application/json');

    // The command always carries a body, so Content-Type stays — unlike the bodyless
    // `monitoringApi` GET above.
    return config;
});
